import { useMemo } from 'react';
import { css } from '@emotion/react';
import { kebabCase } from 'lodash-es';
import { AnimatePresence, motion } from 'motion/react';
import { onlyText } from 'react-children-utilities';
import { useQuery } from 'react-query';
import { DashboardTrackingIds } from '@frontend/dashboard';
import { DashboardWidget, DashboardWidgetFC, useDashboardWidget } from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { SchemaQuickActionAPIService } from '@frontend/schema';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { useModalControl, Text, Button } from '@frontend/design-system';
import { QuickActions, useQuickActions } from './actions';
import { QuickActionChip } from './atoms';
import { regulatedEnabledData } from './helpers';
import { QuickActionsTray } from './quick-actions-tray';
import type { QuickActionEnabledMap } from './types';

const quickActionsTrackingIds = DashboardTrackingIds.featureModule('quick-actions');
const emptyUUID = '00000000-0000-0000-0000-000000000000';

/**
 * @dashboard-widget
 *
 * id: quick-actions
 * title: Quick Actions
 * description: Program all your most-used actions into buttons for easy access.
 * icon: action-bar
 */
export const QuickActionsWidget: DashboardWidgetFC = () => {
  const { t } = useTranslation('quick-actions');
  const { modalProps, triggerProps, closeModal } = useModalControl();
  const { widgetTrackingId } = useDashboardWidget();
  const { getCustomizationFlagValueById } = useScopedAppFlagStore();

  const { data: prefilteredData } = useQuery({
    queryKey: 'quickActions',
    queryFn: () => SchemaQuickActionAPIService.GetQuickActionsOfUser({}),
  });

  const initialData = useMemo(() => {
    if (!prefilteredData) return undefined;

    return {
      ...prefilteredData,
      enabled: prefilteredData.enabled?.filter((label) => label !== 'New Call'),
      all: prefilteredData.all?.filter((item) => item.label !== 'New Call'),
    };
  }, [prefilteredData]);

  const data = useMemo(() => {
    if (!initialData) return undefined;

    return {
      ...initialData,
      enabled: initialData.enabled?.map((label) => (label === 'Office Hours' ? 'Phone Hours' : label)),
      all: initialData.all?.map((item) => ({
        ...item,
        label: item.label === 'Office Hours' ? 'Phone Hours' : item.label,
      })),
    };
  }, [initialData]);

  const enabledData = useMemo(() => regulatedEnabledData(data), [data]);

  const quickActionEnabledMap = useMemo(() => {
    return (
      data?.all?.reduce((acc, val) => {
        const key = val.label;
        const isEmptyCFId = val.cFlagId === emptyUUID;
        if (!key) return acc;
        acc[key] = { hasCustomizationFlag: getCustomizationFlagValueById(val.cFlagId ?? '') || isEmptyCFId };
        return acc;
      }, {} as QuickActionEnabledMap) ?? {}
    );
  }, [getCustomizationFlagValueById, data]);

  const prefilteredActions = useQuickActions();
  const actions = useMemo(() => {
    const filteredActions: QuickActions = {};
    for (const action in prefilteredActions) {
      if (quickActionEnabledMap?.[action]?.hasCustomizationFlag) {
        filteredActions[action] = prefilteredActions[action];
      }
    }
    return filteredActions;
  }, [prefilteredActions, quickActionEnabledMap]);

  return (
    <DashboardWidget>
      <DashboardWidget.Header
        action={{
          label: t('Add Quick Action'),
          onClick: () => triggerProps.onClick(),
          ref: triggerProps.ref,
          trackingId: widgetTrackingId('btn-add-quick-action'),
        }}
      ></DashboardWidget.Header>
      <DashboardWidget.Content
        as={motion.div}
        layoutRoot
        css={[
          css`
            display: grid;
            width: 100%;
            padding-top: ${theme.spacing(2)};
            gap: ${theme.spacing(1)};
            grid-template-columns: repeat(2, 1fr);
          `,
          (enabledData?.length ?? 0) > 10 &&
            css`
              overflow-y: auto;
            `,
        ]}
      >
        <AnimatePresence initial={false}>
          {!enabledData?.length && (
            <QuickActionChip
              trackingId={quickActionsTrackingIds('add-quick-action')}
              onClick={() => triggerProps.onClick()}
              variant='empty'
              leftElement={<Icon name='plus-small' color='primary' />}
            >
              {t('Add Quick Action')}
            </QuickActionChip>
          )}

          {enabledData?.map((action) => {
            const quickAction = actions?.[action];
            if (!quickAction) return null;

            return (
              <QuickActionChip
                trackingId={widgetTrackingId(`btn-chip-${kebabCase(onlyText(action))}`)}
                key={action}
                leftElement={quickAction.icon}
                onClick={quickAction.onClick}
              >
                {quickAction.label}
              </QuickActionChip>
            );
          })}
        </AnimatePresence>
      </DashboardWidget.Content>
      <QuickActionsTray actions={actions} modalProps={modalProps} data={data} closeModal={closeModal} />
    </DashboardWidget>
  );
};

QuickActionsWidget.config = {
  size: 'medium-narrow',
  feature: 'general',
};

export const QuickActionsNavMode = ({ onClick }: { onClick?: () => void }) => {
  const { t } = useTranslation('quick-actions');
  const { modalProps, triggerProps, closeModal } = useModalControl();
  const { getCustomizationFlagValueById } = useScopedAppFlagStore();

  const { data: prefilteredData } = useQuery({
    queryKey: 'quickActions',
    queryFn: () => SchemaQuickActionAPIService.GetQuickActionsOfUser({}),
  });

  const initialData = useMemo(() => {
    if (!prefilteredData) return undefined;

    return {
      ...prefilteredData,
      enabled: prefilteredData?.enabled?.filter((label) => label !== 'New Call'),
      all: prefilteredData?.all?.filter((item) => item.label !== 'New Call'),
    };
  }, [prefilteredData]);

  const data = useMemo(() => {
    if (!initialData) return undefined;

    return {
      ...initialData,
      enabled: initialData.enabled?.map((label) => (label === 'Office Hours' ? 'Phone Hours' : label)),
      all: initialData.all?.map((item) => ({
        ...item,
        label: item.label === 'Office Hours' ? 'Phone Hours' : item.label,
      })),
    };
  }, [initialData]);

  const enabledData = useMemo(() => regulatedEnabledData(data), [data]);

  const quickActionEnabledMap = useMemo(() => {
    return (
      data?.all?.reduce((acc, val) => {
        const key = val.label;
        const isEmptyCFId = val.cFlagId === emptyUUID;
        if (!key) return acc;
        acc[key] = { hasCustomizationFlag: getCustomizationFlagValueById(val.cFlagId ?? '') || isEmptyCFId };
        return acc;
      }, {} as QuickActionEnabledMap) ?? {}
    );
  }, [getCustomizationFlagValueById, data]);

  const prefilteredActions = useQuickActions();
  const actions = useMemo(() => {
    const filteredActions: QuickActions = {};
    for (const action in prefilteredActions) {
      if (quickActionEnabledMap?.[action]?.hasCustomizationFlag) {
        filteredActions[action] = prefilteredActions[action];
      }
    }
    return filteredActions;
  }, [prefilteredActions, quickActionEnabledMap]);

  return (
    <div
      css={css`
        margin-top: ${theme.spacing(4)};
      `}
    >
      <header
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: ${theme.spacing(1, 2)};
        `}
      >
        <Text as='h3' weight='bold' size='medium'>
          {t('My Launchpad')}
        </Text>
        <Button {...triggerProps} variant='tertiary'>
          {t('Add Quick Action')}
        </Button>
      </header>
      <motion.div
        layoutRoot
        layout
        css={[
          css`
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            width: 100%;
            padding: ${theme.spacing(2)};
            background-color: ${theme.colors.neutral5};
            border: 1px solid ${theme.colors.neutral10};
            border-radius: ${theme.borderRadius.medium};
            gap: ${theme.spacing(1)};
          `,
          (enabledData?.length ?? 0) > 10 &&
            css`
              overflow-y: auto;
            `,
        ]}
      >
        <AnimatePresence initial={false}>
          {!enabledData?.length && (
            <QuickActionChip
              trackingId={quickActionsTrackingIds('add-quick-action')}
              onClick={() => triggerProps.onClick()}
              variant='empty'
              leftElement={<Icon name='plus-small' color='primary' />}
            >
              {t('Add Quick Action')}
            </QuickActionChip>
          )}

          {enabledData?.map((action) => {
            const quickAction = actions?.[action];
            if (!quickAction) return null;

            return (
              <QuickActionChip
                key={action}
                leftElement={quickAction.icon}
                onClick={() => {
                  quickAction?.onClick();
                  onClick?.();
                }}
              >
                {quickAction.label}
              </QuickActionChip>
            );
          })}
        </AnimatePresence>
      </motion.div>
      <QuickActionsTray actions={actions} modalProps={modalProps} data={data} closeModal={closeModal} />
    </div>
  );
};

QuickActionsWidget.config = {
  size: 'medium-narrow',
  feature: 'general',
};
